import { motion, AnimatePresence } from "framer-motion";
import React from "react";
import { inter } from "../fonts/inter";
import SplashCursor from "../components/other/SplashCursor";
import ShinyText from "../components/other/ShinyText";
import HeroBackground from "../components/background/HeroBackground";
import SplineBackground from "../components/background/SplineBackground";
import BackgroundToggle from "../components/other/BackgroundToggle";

interface HeroProps {
  backgroundMode: number; // 0 = splash only, 1 = video+text, 2 = spline
  onToggle: () => void;
}

const Hero: React.FC<HeroProps> = ({ backgroundMode, onToggle }) => {
  return (
    <motion.section
      className="relative z-10 flex h-[100vh] w-full justify-center"
      id="home"
      initial="initial"
      animate="animate"
    >
      {/* Background - only one at a time */}
      <AnimatePresence mode="wait">
        {backgroundMode === 0 && (
          <motion.div
            key="splash-bg"
            className="absolute inset-0 bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
        {backgroundMode === 1 && (
          <motion.div
            key="video-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <HeroBackground />
          </motion.div>
        )}
        {backgroundMode === 2 && (
          <motion.div
            key="spline-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SplineBackground />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Splash cursor - only show in mode 0 */}
      <AnimatePresence>
        {backgroundMode === 0 && (
          <motion.div
            key="splash-cursor"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SplashCursor />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Text - show in mode 0 (with splash) and mode 1 (with video) */}
      <AnimatePresence>
        {(backgroundMode === 0 || backgroundMode === 1) && (
          <motion.div
            key="hero-text"
            className="relative z-20 mt-10 flex w-full flex-col items-center justify-center px-4 sm:mt-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div
              className={`relative flex flex-col items-center justify-center ${inter.className} w-full max-w-[95%]`}
            >
              <h1 className="mb-1 w-full text-center text-[40px] font-bold leading-[1.1em] tracking-tighter sm:text-[45px] md:mb-16 md:text-[60px] lg:text-[80px]">
                <ShinyText text="Hi I'm Sahindu Gayanuka." speed={3} />
              </h1>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button - always visible */}
      <BackgroundToggle backgroundMode={backgroundMode} onToggle={onToggle} />
    </motion.section>
  );
};

export default Hero;
